import { z } from 'zod'
import { timeOfDaySchema } from '../validators/time-of-day.validator.js'
import { wallClockSchema } from '../validators/temporal.validator.js'

/**
 * How long a moment's description may be.
 *
 * Room for a paragraph and its line breaks: who walks in which order, which song
 * cues the entrance. It was 200, to keep a run sheet to one line a moment, and a
 * planner ran out of room writing her day (2026-10-22). A description is still
 * the only thing a moment holds besides its time; it can just say more. The
 * database column is unbounded text, so this cap is for reading the day, not for
 * storage. See `docs/event-timeline.md`.
 */
export const TIMELINE_LABEL_MAX = 1000

/** What the field and the API both say when a description is over the cap. */
export const TIMELINE_LABEL_TOO_LONG = `Keep it to ${String(TIMELINE_LABEL_MAX)} characters.`

/** A moment's description, shared by an event's day and a venue's templates. */
export const timelineLabelSchema = z
  .string()
  .trim()
  .min(1, 'Say what happens')
  .max(TIMELINE_LABEL_MAX, TIMELINE_LABEL_TOO_LONG)

const label = timelineLabelSchema

/** A row of a template, as written. The id comes back; the order is the index. */
export const venueTimelineEntryInputSchema = z
  .object({
    label,
    startsAt: timeOfDaySchema,
  })
  .strict()

export type VenueTimelineEntryInput = z.infer<typeof venueTimelineEntryInputSchema>

/**
 * Create a timeline template for a venue.
 *
 * Entries arrive as an ordered list and their `sortOrder` is their index — the
 * order of the array is the running order, which is what a template means. The
 * caller never computes a sort key.
 */
export const createVenueTimelineTemplateInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    entries: z.array(venueTimelineEntryInputSchema).max(200).default([]),
    /** Omit for the rule that the first template of a venue is its default. */
    isDefault: z.boolean().optional(),
  })
  .strict()

export type CreateVenueTimelineTemplateInput = z.infer<typeof createVenueTimelineTemplateInputSchema>

/**
 * Update a template — rename it, replace its rows, or make it the default.
 *
 * `entries` replaces the whole list rather than patching rows. The order is the
 * meaning, so a partial update would need a way to say "and this one moved",
 * which is a harder thing to get right than sending the list that is on screen.
 */
export const updateVenueTimelineTemplateInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120).optional(),
    entries: z.array(venueTimelineEntryInputSchema).max(200).optional(),
    /**
     * Only `true` is meaningful. A venue's default is changed by naming the new
     * one, never by un-setting the old — a venue with no default seeds nothing,
     * and arriving there by clearing a checkbox is not a thing anyone means.
     */
    isDefault: z.literal(true).optional(),
  })
  .strict()

export type UpdateVenueTimelineTemplateInput = z.infer<typeof updateVenueTimelineTemplateInputSchema>

/**
 * A moment on an event's timeline.
 *
 * `startsAt` is a wall-clock time on the event's date — the same floating clock
 * an event and its phases use, validated and branded here so nothing downstream
 * has to re-check it. It is not converted on the way in or the way out.
 */
export const eventTimelineEntryInputSchema = z
  .object({
    label,
    startsAt: wallClockSchema,
  })
  .strict()

export type EventTimelineEntryInput = z.infer<typeof eventTimelineEntryInputSchema>

/** Add one moment to an event's day. */
export const createEventTimelineEntryInputSchema = eventTimelineEntryInputSchema

export type CreateEventTimelineEntryInput = z.infer<typeof createEventTimelineEntryInputSchema>

/** Change a moment: what it is, or when. Omitted fields are left alone. */
export const updateEventTimelineEntryInputSchema = z
  .object({
    label: label.optional(),
    startsAt: wallClockSchema.optional(),
  })
  .strict()

export type UpdateEventTimelineEntryInput = z.infer<typeof updateEventTimelineEntryInputSchema>

/**
 * Replace an event's timeline with a venue template's.
 *
 * Applying replaces rather than appends: two timelines concatenated describe no
 * day at all. The web asks first when there is something to lose.
 */
export const applyVenueTimelineTemplateInputSchema = z
  .object({
    templateId: z.string().uuid(),
  })
  .strict()

export type ApplyVenueTimelineTemplateInput = z.infer<typeof applyVenueTimelineTemplateInputSchema>
